/**
 * I18nContext, provides the bound translator + active language to
 * every dashboard component.
 *
 * The catalogue comes from /api/platform/me (resolved server-side
 * to global defaults + super-admin overrides + tenant overrides).
 * The active language follows the same source chain platform-ui
 * already uses for the chat language picker, localStorage > user
 * pick > 'en'.
 *
 * Components consume via useT():
 *
 *     const t = useT();
 *     return <button>{t('composer.send')}</button>;
 *
 * If the provider isn't mounted (unit tests, early bootstrap), the
 * hook falls back to the built-in English catalogue so nothing
 * renders "undefined" or a raw key.
 */
import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import {
  buildTranslator,
  BUILTIN_EN,
  type LangCode,
  type PlatformUiI18nPayload,
  type StringKey,
  type Translator,
} from './i18n';

interface I18nValue {
  lang: LangCode;
  t: Translator;
}

// Default value: English, built-in catalogue. Used when no provider
// is mounted (test envs, very first paint). Keeps components from
// rendering raw keys.
const DEFAULT_VALUE: I18nValue = {
  lang: 'en',
  t: (key: StringKey, vars?: Record<string, string>) => {
    let out = BUILTIN_EN[key] ?? String(key);
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        out = out.split(`{${k}}`).join(v);
      }
    }
    return out;
  },
};

const I18nContext = createContext<I18nValue>(DEFAULT_VALUE);

/** Wrap the dashboard tree once near the top. `catalogue` is the
 *  `platform_ui_i18n` field from /me; `lang` is the user's active
 *  dashboard language. The provider memoises the translator so
 *  re-renders don't rebuild the dict on every paint. */
export function I18nProvider({
  catalogue,
  lang,
  children,
}: {
  catalogue: PlatformUiI18nPayload;
  lang: LangCode;
  children: ReactNode;
}) {
  const value = useMemo<I18nValue>(
    () => ({
      lang,
      t: buildTranslator(lang, catalogue),
    }),
    [lang, catalogue],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Returns a translator bound to the active language. Use this in
 *  every component that renders user-facing chrome strings.
 *  Components that ALSO need the current language code (RTL handling,
 *  language picker showing the active selection) can read it via
 *  useI18nLang() below. */
export function useT(): Translator {
  return useContext(I18nContext).t;
}

export function useI18nLang(): LangCode {
  return useContext(I18nContext).lang;
}
